import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Reveal from '../components/marketing/Reveal';
import SectionHead from '../components/marketing/SectionHead';
import DemoCTA from '../components/marketing/DemoCTA';
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { usePosts } from '../cms/blog';

const BlogPage = () => {
  // Seeded posts merged with anything published from the content console.
  const blogPosts = usePosts();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const upcomingTopics = [
    'Constrained Competence: Why Healthcare AI Must Know What It Doesn\'t Know',
    'Why Your Board Needs an Air-Gapped Question Bank',
    'From Presentation to Assessment in 10 Minutes',
    'AI Proctoring vs. Human Proctoring: Why We Use Both',
    'The 10 Billion Sensors Principle: Why AI Can\'t Replace Clinical Judgment',
  ];

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await addDoc(collection(db, 'newsletter_signups'), {
        email,
        source_page: '/blog',
        timestamp: serverTimestamp(),
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error subscribing:', err);
      setError('Something went wrong. Please email john@edaisystems.com instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Insights | EdAI Systems</title>
        <meta
          name="description"
          content="Thought leadership on medical board education, AI in assessment, constrained competence, and the future of professional certification."
        />
        <meta property="og:image" content="https://edaisystems.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://edaisystems.com/og-image.png" />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16">
        {/* Hero */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <SectionHead
                badge="Insights"
                badgeColor="accent-indigo"
                title="Perspectives on Medical Board Education"
                subtitle="Thought leadership on AI in assessment, constrained competence, and the future of professional certification."
              />
            </Reveal>
          </div>
        </section>

        {/* Published Posts Section */}
        <Reveal delay={100}>
          <section className="py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-text-primary mb-8">Published</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {blogPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group p-6 rounded-2xl border transition-all duration-300 hover:scale-[1.02]"
                    style={{
                      backgroundColor: 'rgba(12,18,32,0.7)',
                      borderColor: 'rgba(51,65,85,0.18)',
                      boxShadow: '0 12px 80px -12px rgba(174, 182, 197, 0.5), 0 0 60px rgba(56,189,248,0.06)',
                    }}
                  >
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-xs font-mono rounded mb-3"
                      style={{
                        backgroundColor: post.categoryColor === 'accent-cyan' ? 'rgba(56,189,248,0.1)'
                          : post.categoryColor === 'accent-red' ? 'rgba(239,68,68,0.1)'
                          : 'rgba(251,191,36,0.1)',
                        color: post.categoryColor === 'accent-cyan' ? '#38bdf8'
                          : post.categoryColor === 'accent-red' ? '#ef4444'
                          : '#fbbf24',
                      }}
                    >
                      {post.category}
                    </span>
                    <h3 className="text-lg font-semibold text-text-primary group-hover:text-accent-cyan transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-text-secondary text-sm line-clamp-2 mb-4">
                      {post.description}
                    </p>
                    <div className="flex items-center gap-2 text-text-muted text-xs">
                      <span>{post.author}</span>
                      <span>·</span>
                      <span>{formatDate(post.date)}</span>
                      <span>·</span>
                      <span>{post.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* Coming Soon Section */}
        <Reveal delay={100}>
          <section className="py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-text-primary mb-4">Coming Soon</h2>
                <p className="text-lg text-text-secondary">
                  More articles in development:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcomingTopics.map((topic) => (
                  <div
                    key={topic}
                    className="p-6 rounded-xl border"
                    style={{
                      backgroundColor: 'rgba(12,18,32,0.7)',
                      borderColor: 'rgba(51,65,85,0.18)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-accent-indigo mt-0.5">◆</span>
                      <span className="text-text-secondary">{topic}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* Newsletter Signup */}
        <Reveal delay={100}>
          <section className="py-24 px-4 sm:px-6 lg:px-8 bg-bg-card/30">
            <div className="max-w-2xl mx-auto">
              <div
                className="p-8 md:p-12 rounded-2xl border text-center"
                style={{
                  backgroundColor: 'rgba(12,18,32,0.7)',
                  borderColor: 'rgba(51,65,85,0.18)',
                }}
              >
                {isSubmitted ? (
                  <>
                    <div className="w-16 h-16 bg-accent-emerald/20 text-accent-emerald rounded-full flex items-center justify-center text-3xl mx-auto mb-6">
                      ✓
                    </div>
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      Thanks!
                    </h3>
                    <p className="text-text-secondary">
                      We'll notify you when new articles publish.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-text-primary mb-4">
                      Get Notified
                    </h3>
                    <p className="text-text-secondary mb-8">
                      Get notified when new articles publish.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="flex-1 px-4 py-3 bg-bg-primary border border-border-subtle rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-cyan transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-accent-cyan text-bg-primary font-semibold rounded-lg hover:bg-accent-cyan/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                      </button>
                    </form>

                    {error && (
                      <div className="text-accent-rose text-sm mt-4">{error}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </Reveal>

        {/* Demo CTA */}
        <Reveal delay={100}>
          <section className="py-24 px-4 sm:px-6 lg:px-8">
            <DemoCTA source="blog-page" />
          </section>
        </Reveal>
      </div>
    </>
  );
};

export default BlogPage;
